import { useEffect, useState } from "react";
import { api } from "../lib/api";
import ProductCard from "./ProductCard";

export default function Catalogue({ tags, onConcern, onEnquirePackage }) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [products, setProducts] = useState([]);
  const [extra, setExtra] = useState({ concerns: [], packages: [] });

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Product grid: server-side tag filter + search.
  useEffect(() => {
    let active = true;
    api.products({ tag: activeFilter, q: debounced }).then((d) => active && setProducts(d.products));
    return () => {
      active = false;
    };
  }, [activeFilter, debounced]);

  // Unified search surfaces related concerns + packages alongside products.
  useEffect(() => {
    let active = true;
    if (!debounced) {
      setExtra({ concerns: [], packages: [] });
      return;
    }
    api.search(debounced).then((d) => active && setExtra({ concerns: d.concerns, packages: d.packages }));
    return () => {
      active = false;
    };
  }, [debounced]);

  const chips = [{ key: "all", label: "All" }, ...tags];
  const hasExtra = debounced && (extra.concerns.length > 0 || extra.packages.length > 0);

  return (
    <section id="products" className="section products">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Our shop</span>
          <h2>Natural products catalogue</h2>
          <p>Search across products, concerns and packages — or filter by concern. Add what you need to your cart.</p>
        </div>

        <div className="shop-controls">
          <div className="search-wrap">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, concerns & packages — e.g. diabetes, tea, immunity…"
            />
          </div>
        </div>

        <div className="filter-chips">
          {chips.map((c) => (
            <button
              key={c.key}
              className={`chip${activeFilter === c.key ? " active" : ""}`}
              onClick={() => setActiveFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {hasExtra && (
          <div style={{ marginBottom: "22px", display: "grid", gap: "12px" }}>
            {extra.concerns.length > 0 && (
              <div>
                <span className="eyebrow">Related concerns</span>
                <div className="filter-chips" style={{ marginTop: "8px", marginBottom: 0 }}>
                  {extra.concerns.map((c) => (
                    <button key={c.id} className="chip" onClick={() => onConcern(c.id)}>
                      <i className={`fa-solid ${c.icon}`} /> {c.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {extra.packages.length > 0 && (
              <div>
                <span className="eyebrow">Related packages</span>
                <div className="filter-chips" style={{ marginTop: "8px", marginBottom: 0 }}>
                  {extra.packages.map((p) => (
                    <button key={p.id} className="chip" onClick={() => onEnquirePackage(p.name)}>
                      <i className={`fa-solid ${p.icon}`} /> {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="products-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
        <div className={`no-results${products.length === 0 ? " show" : ""}`}>
          <i className="fa-solid fa-leaf" style={{ fontSize: "2rem", display: "block", marginBottom: "10px" }} />
          No products match your search. Try another term.
        </div>
      </div>
    </section>
  );
}
